import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse} from "@angular/common/http";
import { DatabaseService } from "./database.service";

import { Language, MotesPaths } from "../models";
import { SendVerificationEmailBody } from '../api-models/user';
import { MotesLoginService } from "../motes-login";
import { firstValueFrom } from 'rxjs';
import { EventMote, StoryMote } from '../api-models/event-mote';
import { SendUpgradeEmailBody } from '../api-models/project';
import { MoteTranslationBody, MoteTranslationResponse } from '../api-models/mote';
import { SyncStripeProductRsp, SyncStripePriceRsp, SyncStripePriceBody, SyncStripeProductBody } from '../api-models/products';
import { CreatePartnerBody, CreatePartnerRsp, Partner } from '../api-models/partner';
import { ClearCdnBody, ClearCdnResponse } from '@motes-sdk/api-models/cdn';
import { SupabaseService } from './supabase.service';
import { environment } from '@env/environment';

export interface MoteKeyRsp { status: string, data: { moteKey: string } };
export interface CopyMoteRsp { status: string, data: { moteKey: string } };

export interface CopyMoteOptions {
    /** Target project key. If different from the source project the mote is moved (parent/products cleared). */
    newProjectKey?: string;
    /** Explicit parent to set on the copy (e.g. re-parent a copied story onto a copied tour). */
    newParentKey?: string;
    /** Suggested key for the copy (a unique key is derived from it, falling back to the title). */
    suggestedMoteKey?: string;
    /** Reset the copy to an unpublished draft (clears publishedOn + previewKey, fresh timestamps). */
    resetPublishState?: boolean;
    /** Clear the translation origin so the copy is a standalone mote. */
    clearOrigin?: boolean;
    /** Do not error if the source mote does not exist. */
    ignoreNonExisting?: boolean;
}

/**
 * Generic request execution function to perform batch requests (in order to delay the execution)
*/
export type DelayedRequest<T> = () => Promise<T>;

// Max allowed StoryHunt API request time in ms - OBS 1 min is max allowed by firebase gateway (and cannot be changed)
// see https://stackoverflow.com/questions/50519766/gateway-timeout-for-https-functions-proxied-through-firebase-hosting
// However motesExpress firebase function is allowed to run 5 min (it just need to return responses withing 1 min)
const MAX_REQUEST_TIMEOUT = 60000 * 6;
// Functions timeout is currently set to 9min
const MAX_FUNCTION_TIMEOUT = 540000;

@Injectable({ providedIn: 'root' })
export class RestService {
    constructor(private httpClient: HttpClient, private db: DatabaseService, private loginService: MotesLoginService, private supabaseService: SupabaseService ) {
    }

    public async getUniqueKey(moteKey: string): Promise<string> {
        // Get authenticated user token (must be superAdmin)
        let authOptions = await this.getAuthOptions();

        // Append suggested mote key
        let params = new HttpParams();
        params = params.append('suggestedMoteKey', moteKey);

        let json = await this.httpClient.get<MoteKeyRsp>(MotesPaths.MOTES_REST + "/api/generate-mote-key", { params: params, headers: authOptions }).toPromise().catch(err => {
            console.log("Could not get mote key: ", err);
            null;
        });
        // Return available unique key
        return json ? json.data.moteKey : "";
    }

    /**
     * Copy a single mote (server-side, incl. media) and return the new mote key.
     * Copies only the given mote — for a whole tour, copy the tour then each child
     * with `newParentKey` set to the copied tour key.
     * @param moteKey Source mote to copy
     * @param options Copy behaviour (target project, new parent, reset/clear flags)
     * @returns The key of the new copy, or "" on failure
     */
    public async copyMote(moteKey: string, options: CopyMoteOptions = {}): Promise<string> {
        if(!moteKey) return "";
        // Get authenticated user token (must be admin / super admin)
        const authOptions = await this.getAuthOptions();

        const body = {
            moteKey,
            suggestedMoteKey: options.suggestedMoteKey || "",
            newProjectKey: options.newProjectKey || "",
            newParentKey: options.newParentKey || "",
            resetPublishState: options.resetPublishState ?? false,
            clearOrigin: options.clearOrigin ?? false,
            ignoreNonExisting: options.ignoreNonExisting ?? false,
        };

        const json = await this.httpClient.post<CopyMoteRsp>(MotesPaths.MOTES_REST + "/api/copy-mote", body, { headers: authOptions }).toPromise().catch(err => {
            console.log("Could not copy mote: ", err);
            return null;
        });
        // Return the key of the new copy
        return json?.data?.moteKey || "";
    }

    public async createProject(title: string, text: string): Promise<{ projectKey: string, status: number }> {
        // Get authenticated user token
        let authOptions = await this.getAuthOptions();
        let body = { 'title': title, 'text': text };

        let rsp = await this.httpClient.post<{ projectKey: string }>(MotesPaths.MOTES_REST + "/v1/projects/actions/create", body, {
            headers: authOptions,
        }).toPromise().catch(err => {
            console.log("Could not create project: ", err);
            return err;
        });

        return rsp;
    }

    public async getStreetViewImage(lat: number, long: number): Promise<File> {
        // Get authenticated user token
        let authOptions = await this.getAuthOptions();
        // Construct StroyHunt street view endpoint url (use all the great default values for heading, pitch fov etc.)
        const url = `${MotesPaths.MOTES_REST}/v1/image/streetview?location=${lat},${long}`;
        // Get result as image blob
        let imageBlob: Blob = await this.httpClient.get(url, { headers: authOptions, responseType: 'blob' }).toPromise().catch(err => {
            console.error("Could not get Street View image: ", err);
            return null;
        });
        // Return null in case no street view image was found at the specified location
        if(!imageBlob) return null;

        // Get content type from blob
        const contentType = imageBlob.type;
        // Split content type (ie. image/jpeg) to get file extension
        const [_, ext] = imageBlob.type?.split("/");
        // Create a file for easier handling / uploading image later
        return new File([imageBlob], `streetview.${ext}`, { type: contentType });
    }

    /**
     * Create Stripe Connect account and associate it with a world
     */
    public async createWorldPartnerAccount(projectKey: string, tourKey: string): Promise<CreatePartnerRsp> {
        const headers = await this.loginService.getAuthOptions();
        const body: CreatePartnerBody = { projectKey, tourKey };

        const req = this.httpClient.post<CreatePartnerRsp>(`${MotesPaths.MOTES_REST}/v1/partner`, body, headers);

        return req.toPromise().then((rsp) => {
            console.log("Partner created: ", rsp);
            return rsp;
        }).catch(err => {
            console.log("Could not make Stripe link for partner creation: ", err);
            return null;
        });
    }

    /**
     * Create Stripe Connect account (used in home partner-signup page)
     */
    public async createPartnerAccount(secretCodeInput: string): Promise<CreatePartnerRsp> {

        // Append secret code input
        let params = new HttpParams().append('secretCodeInput', secretCodeInput);

        // For test use path: http://localhost:<port>/api/partners/create-account
        let json = await this.httpClient.post<CreatePartnerRsp>(MotesPaths.MOTES_REST + "/api/partners/create-account", params).toPromise().catch(err => {
            console.log("Could not create account links: ", err);
            return null;
        });

        return json;
    }

    public async getPartnerAccount(partnerId: string): Promise<Partner> {
        if(!partnerId) return null;
        // Get authenticated user token (must be admin)
        const headers = await this.loginService.getAuthOptions();

        const rsp = await this.httpClient.get<Partner>(`${MotesPaths.MOTES_REST}/v1/partner/${partnerId}`, headers).toPromise().catch(err => {
            console.log("Could not retrieve Stripe partner account.", err);
            return null;
        });
        return rsp;
    }

    public async createSendInBlueContact(name: string, email: string): Promise<JSON> {
        let json = await this.httpClient.post<JSON>(MotesPaths.MOTES_REST + "/api/contact/create", {
            'name': name,
            'email': email
        }).toPromise().catch(err => {
            console.log("Could not create SendInBlue contact: ", err);
            return null;
        });
        return json;
    }

    public async sendPartnerInvoice(partnerId: string, partnerEmail: string, invoiceStart: number, invoiceEnd: number): Promise<JSON> {
        // Get authenticated user token (must be superAdmin)
        let headers = await this.loginService.getAuthOptions();

        let json = await this.httpClient.post<JSON>(MotesPaths.MOTES_REST + "/api/partners/send-invoice", {
            'partnerId': partnerId,
            'partnerEmail': partnerEmail,
            'invoiceStart': invoiceStart,
            'invoiceEnd': invoiceEnd
        }, headers).toPromise().catch(err => {
            console.log("Could not send partner invoice: ", err);
            return null;
        });
        return json;
    }

    /**
     * Create or update existing Stripe product title, description and image
     */
    public async syncStripeProduct(stripeProductId: string, productTitle: string, productDescription: string, productSquare: string, projectKey: string): Promise<SyncStripeProductRsp> {
        // Get authenticated user token (must be admin)
        const headers = await this.loginService.getAuthOptions();
        const body: SyncStripeProductBody = { stripeProductId, productTitle, productDescription, productSquare, projectKey };

        const rsp = await this.httpClient.post<SyncStripeProductRsp>(MotesPaths.MOTES_REST + "/v1/products/sync-stripe-product", body, headers).toPromise().catch(err => {
            console.log("Could not create Stripe product.", err);
            return null;
        });
        return rsp;
    }

    /**
     * Delete Stripe product (only possible if it doesn't contain any prices)
     */
    public async deleteStripeProduct(stripeProductId: string): Promise<boolean> {
        const headers = await this.loginService.getAuthOptions();
        const rsp = await this.httpClient.post<SyncStripeProductRsp>(MotesPaths.MOTES_REST + `/v1/products/${stripeProductId}/actions/delete`, {}, headers).toPromise().catch(err => {
            console.log("Could not delete Stripe product.", err);
            return false;
        }).then(() => true);
        return rsp;
    }

    /**
     * Create or get existing Stripe price
     */
    public async syncStripePrice(productPrice: number, stripeProductId: string, projectKey: string): Promise<SyncStripePriceRsp> {
        // Get authenticated user token (must be superAdmin)
        const headers = await this.loginService.getAuthOptions();
        const body: SyncStripePriceBody = { productPrice, stripeProductId, projectKey };

        const rsp = await this.httpClient.post<SyncStripePriceRsp>(MotesPaths.MOTES_REST + "/v1/products/sync-stripe-price", body, headers).toPromise().catch(err => {
            console.log("Could not create Stripe price", err);
            return null;
        });
        return rsp;
    }

    /**
     * Send verification email to users created with email (not Google, Apple or Fb)
     * @returns true on success
     */
    public async sendVerificationEmail(userKey: string, userName: string, userEmail: string): Promise<boolean> {
        const authOptions = await this.getAuthOptions();
        const endPoint = MotesPaths.USER_REST_API(userKey, 'send-verification-email');
        const body: SendVerificationEmailBody = { userEmail, userName };

        let rsp = await this.httpClient.post<void>(endPoint, body, { headers: authOptions, observe: 'response' }).toPromise().catch(err => {
            console.log("Could not send verification email: ", err);
            return err as HttpErrorResponse;
        });

        return rsp.ok;
    }

    /**
     * Promote a user to become admin user
     * Provide token for users to be verified first
     * @returns true on success
     */
    public async promoteUserToAdmin(userKey: string, token?: string): Promise<boolean> {
        const authHeader = await this.getAuthOptions();
        const endPoint = MotesPaths.USER_REST_API(userKey, 'promote-admin');
        const body = { token: token || "" };

        const rsp = await this.httpClient.patch<void>(endPoint, body, { headers: authHeader, observe: 'response' }).toPromise().catch(err => {
            console.log("Could not promote user to admin: ", err);
            return err as HttpErrorResponse;
        });
        return rsp.ok;
    }

    public async getAuthOptions(): Promise<HttpHeaders> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': [await this.loginService.getIdToken()]
        });
        return headers;
    }

    public async getSupabaseAuthOptions(): Promise<HttpHeaders> {
        const firebaseToken = await this.loginService.getIdToken();
        const supabaseToken = await this.supabaseService.mintSupabaseToken(firebaseToken);

        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseToken}`,
            'firebase-token': firebaseToken
        });
        return headers;
    }

    public async sendCreatorUpgradeRequest(name: string, email: string, phoneNumber: string, msg?: string): Promise<boolean> {
        const authOptions = await this.getAuthOptions();
        const body: SendUpgradeEmailBody = { name, email, phoneNumber, msg };

        let rsp = await this.httpClient.post<boolean>(MotesPaths.MOTES_REST + "/v1/projects/actions/send-upgrade-email", body, { headers: authOptions, observe: 'response' }).toPromise().catch(err => {
            console.log("Could not send upgrade email: ", err);
            return err as HttpErrorResponse;
        });

        return rsp.ok;
    }

    /**
     * Translate a mote using the StoryHunt API
     * @param moteKey Mote to be translated
     * @param language Language to translate mote to
     * @param parentKey Optional parent key (aka tour key) to be set on the translated mote
     */
    public async translateTourRequest(moteKey: string, language: Language, doTranslateChildren: boolean = true, parentKey = "") {
        // Create translation request
        const endpoint = `${environment.STORYHUNT_LRP_API}/tours/${moteKey}/actions/translate`;
        let body: MoteTranslationBody = { language, doTranslateChildren };
        if(parentKey) body.parentKey = parentKey;

        // Get auth headers
        const headers = await this.getSupabaseAuthOptions();

        // Set long request timeout for translation
        let rsp = await firstValueFrom(this.httpClient.post<MoteTranslationResponse>(endpoint, body, { headers: headers })).catch((err: HttpErrorResponse) => {
            console.log("Translate request did not succeed...", err?.message);
            return { originKey: moteKey, translatedKey: '', progressPath: '', jobId: ''};
        });
        return rsp;
    }

    /**
     * Re-translate a tour using the StoryHunt API. All stories will be deleted and new translations from the origin will be added.
     * @param tourKey Tour key of target tour to be re-translated
     */
    public async updateTourTranslationRequest(tourKey: string) {
        // Create translation request
        const endpoint = `${environment.STORYHUNT_LRP_API}/tours/${tourKey}/actions/update-translation`;
        // Get auth headers
        const headers = await this.getSupabaseAuthOptions();
        // Start translation process
        let rsp = await firstValueFrom(this.httpClient.patch<MoteTranslationResponse>(endpoint, {}, { headers: headers })).catch((err: HttpErrorResponse) => {
            console.log("Translation update request did not succeed...", err?.message);
            return { originKey: tourKey, translatedKey: '', progressPath: '', jobId: '' };
        });
        return rsp;
    }

    /**
     * Split a list of delayed requests into batches an execute them
     * @param requestList List of requests to be batch executed
     * @param batchSize Size of parallel requests (requests per batch)
     * @returns List of responses
     */
    public async executeBatchRequests<ResponseType>(requestList: DelayedRequest<ResponseType>[], batchSize: number = 3) : Promise<ResponseType[]> {
        let responses: ResponseType[] = [];
        const batchRequestList = this.batchRequests(requestList, batchSize);

        for(let batch of batchRequestList) {
            // Start all requests in batch
            const requests: Promise<ResponseType>[] = batch.map( async (request) => request());
            // Wait for all responses to finish
            const batchResponses = await Promise.all(requests)
            responses.push(...batchResponses);
        }
        return responses;
    }

    /**
     * Split a list of requests into batches to be executed in parallel
     * @param array Array of requests to batched / chunked
     * @param batchSize Size of each batch
     * @returns An array of batches of requests
     */
    private batchRequests<RequestType>(array: RequestType[], batchSize: number): RequestType[][] {
        if(batchSize <= 0) return [];

        const batchList: RequestType[][] = [];
        // Loop though all requests
        for(let i = 0; i < array.length; i += batchSize) {
            // Add batch with x number of requests
            batchList.push(array.slice(i, i + batchSize));
        }
        return batchList;
    }

     /**
     * Execute a list of requests concurrently / in parallel. If the size of the requestList is bigger than
     * the maxConcurrent requests they will be queued until the there is capacity to execute them.
     * @param array Array of requests to be executed
     * @param maxConcurrent Max number of concurrent requests
     * @returns An array of responses when all requests that have been executed
     */
    public async executeConcurrentRequests<ResponseType>(requestList: DelayedRequest<ResponseType>[], maxConcurrent: number = 3): Promise<ResponseType[]> {
        let responseList: ResponseType[] = [];
        let requestQueue: { id: number, promise: Promise<ResponseType>; }[] = [];
        let idx = 0;

        // Run through all requests in list and continue until queue is empty
        while(idx < requestList.length || requestQueue.length > 0) {
            // Add request to queue
            if(idx < requestList.length) {
                // Assign idx as ID to each promise to be able to restore order and add it to queue
                requestQueue.push({ id: idx, promise: requestList[idx]() });
                idx++;
            }
            // Await queue to finish
            if(requestQueue.length == maxConcurrent || idx == requestList.length) {
                // Wait for one request to finish
                const finishedRequest = await Promise.race(requestQueue.map(req => req.promise.then( (rsp) => {
                    return { id: req.id, rsp: rsp};
                })));

                // Save response in the correct order
                responseList[finishedRequest.id] = finishedRequest.rsp;
                // Remove the finished request from the queue
                requestQueue = requestQueue.filter(request => request.id != finishedRequest.id);
            }
        }
        return responseList;
    }

    public async optimizeMoteKey(tourKey: string) {
        const authOptions = await this.getAuthOptions();
        const response = await firstValueFrom(this.httpClient.post<{ newKey: string }>(
            `${MotesPaths.MOTES_REST}/v1/motes/${tourKey}/optimize-key?dryRun=false`,
            { headers: authOptions },
        ));

        return response;
    }

    /**
     * Publish or unpublish a tour
     */
    public async publishTour(tourKey: string, publish: boolean) : Promise<string> {
        const authOptions = await this.getAuthOptions();
        const response = await firstValueFrom(this.httpClient.patch<{ status: string }>(
            `${MotesPaths.MOTES_REST}/v1/tours/${tourKey}/publish`,
            { publish },
            { headers: authOptions },
        ));
        return response?.status;
    }

    /**
     * Sync a tour to the new database (PTG). Omit options for legacy `{ key }`-only sync on the server.
     */
    public async syncTourNewDb(tourKey: string, options?: { published?: boolean; preview?: boolean }): Promise<string> {
        const authOptions = await this.getAuthOptions();
        const body: { published?: boolean; preview?: boolean } = {};
        if (options?.published === true) {
            body.published = true;
        } else if (options?.published === false) {
            body.published = false;
        }
        if (options?.preview === true) {
            body.preview = true;
        }
        try {
            const response = await firstValueFrom(this.httpClient.post<{ status: string }>(
                `${MotesPaths.MOTES_REST}/v1/tours/${tourKey}/actions/sync-new-db`,
                body,
                { headers: authOptions },
            ));
            return response?.status;
        } catch (error) {
            console.error("syncTourNewDb failed", { tourKey, options, error });
            throw error;
        }
    }

    /**
     * Sync a tour to Webflow using the server-side locale and plan rules.
     */
    public async syncWebflowAfterPublish(tourKey: string, body?: { countryId?: string; cityId?: string }): Promise<{ status: string; data?: any; extraData?: any; webflow?: { action?: string; webflowId?: string } }> {
        const authOptions = await this.getAuthOptions();
        const response = await firstValueFrom(this.httpClient.post<{ status: string; data?: any; extraData?: any; webflow?: { action?: string; webflowId?: string } }>(
            `${MotesPaths.MOTES_REST}/v1/tours/${tourKey}/actions/export-webflow`,
            body || {},
            { headers: authOptions },
        ));
        return response;
    }

    /**
     * Accumulate review scores for a tour (writes to mote.accumulatedAppReviews, accumulatedTourReviews)
     */
    public async accumulateReviewsForTour(tourKey: string): Promise<{ status: string; accumulatedAppReviews?: { avg: number; count: number; updatedOn?: number }; accumulatedTourReviews?: { avg: number; count: number; updatedOn?: number } }> {
        const authOptions = await this.getAuthOptions();
        const response = await firstValueFrom(this.httpClient.post<{ status: string; accumulatedAppReviews?: { avg: number; count: number; updatedOn?: number }; accumulatedTourReviews?: { avg: number; count: number; updatedOn?: number } }>(
            `${MotesPaths.MOTES_REST}/v1/tours/${tourKey}/actions/accumulate-reviews`,
            {},
            { headers: authOptions },
        ));
        return response;
    }

    /**
     * Create a new story via the API (support for uploading external images)
     */
    public async createStory(story: StoryMote) : Promise<EventMote> {
        const authOptions = await this.getAuthOptions();
        const response = await firstValueFrom(this.httpClient.post<EventMote>(
            `${MotesPaths.MOTES_REST}/v1/stories/`,
            JSON.stringify(story),
            { headers: authOptions },
        ));
        return response;
    }

    /**
     * Clear the CDN cache of a tour page
     */
    public async clearTourCdnCache(tourKey: string, clearFrontPages: boolean) : Promise<ClearCdnResponse[]> {
        const authOptions = await this.getAuthOptions();
        const body: ClearCdnBody = { clearFrontPages : clearFrontPages };
        // CLear CDN cache of tour
        const response = await firstValueFrom(this.httpClient.put<ClearCdnResponse[]>(
            `${MotesPaths.MOTES_REST}/v1/cdn/${tourKey}/actions/clear`,
            JSON.stringify(body),
            { headers: authOptions },
        ));
        return response;
    }

    /**
     * Populate the CDN cache of a home/tour pages (omit preview and startTour links)
     */
    public async populateTourCdnCache(urlList: string[]): Promise<any> {
        if(!urlList || urlList.length <= 0) return;
        // Filter out preview and startTour urls - they are not that important
        const clearList = urlList.filter( url => Boolean(!url.includes('preview') && !url.includes("startTour")));

        const requestList = clearList.map( (url) => {
            // Populate CDN - omit credentials / cookies to generate correct cache hit
            return fetch(url, { "credentials": "omit" } );
        });
        return await Promise.all(requestList).catch( err => console.log("Ignoring expected CORS fetch error (it populates CDN cache anyway)"));
    }
}
